import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/apiServices';


const initialState = {
  permissions: [],
  currentPermission: null,
  loading: false,
  error: null,
  moduleFilters: [],
  bulkAssignStatus: {
    loading: false,
    error: null,
    success: false
  }
};

export const fetchPermissions = createAsyncThunk(
  'permission/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('permissions');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const fetchPermissionsByRole = createAsyncThunk(
  'permission/fetchPermissionsByRole',
  async (roleId, { rejectWithValue }) => {
    try {
      // Hit the updated endpoint that correctly handles Role ID
      const response = await apiService.get(`permissions/${roleId}`);
      // Backend returns either array (if role) or object (if module legacy). 
      // We expect array here.
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const createPermission = createAsyncThunk(
  'permission/createPermission',
  async (permissionData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('permissions', permissionData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const updatePermission = createAsyncThunk(
  'permission/updatePermission',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`permissions/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const deletePermission = createAsyncThunk(
  'permission/deletePermission',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.delete(`permissions/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const bulkCreatePermissions = createAsyncThunk(
  'permission/bulkCreate',
  async (permissionsData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('permissions/bulk', permissionsData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);


export const bulkCreateAndAssignToRole = createAsyncThunk(
  'permission/bulkCreateAndAssign',
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const response = await apiService.post('permissions/bulk-assign', {
        roleId,
        permissions
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bulk assign failed');
    }
  }
);

const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {
    setCurrentPermission: (state, action) => {
      state.currentPermission = action.payload;
    },
    clearPermissionError: (state) => {
      state.error = null;
    },
    setModuleFilters: (state, action) => {
      state.moduleFilters = action.payload;
    },
    clearBulkAssignStatus: (state) => {
      state.bulkAssignStatus = {
        loading: false,
        error: null,
        success: false
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Permissions (Global)
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Permissions By Role
      .addCase(fetchPermissionsByRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissionsByRole.fulfilled, (state, action) => {
        state.loading = false;
        // Verify payload is array before assigning. 
        // If it's an object (legacy module result), wrap in array or handle? 
        // Our controller update ensures [] if not found or array if found for role.
        state.permissions = Array.isArray(action.payload) ? action.payload : [action.payload];
      })
      .addCase(fetchPermissionsByRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Permission
      .addCase(createPermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions.push(action.payload);
      })
      .addCase(createPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Permission
      .addCase(updatePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePermission.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = state.permissions.map(permission =>
          permission._id === action.payload._id ? action.payload : permission
        );
        if (state.currentPermission?._id === action.payload._id) {
          state.currentPermission = action.payload;
        }
      })
      .addCase(updatePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Permission
      .addCase(deletePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePermission.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = state.permissions.filter(
          permission => permission._id !== action.payload
        );
        if (state.currentPermission?._id === action.payload) {
          state.currentPermission = null;
        }
      })
      .addCase(deletePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk Create
      .addCase(bulkCreatePermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = [...state.permissions, ...action.payload];
      })
      .addCase(bulkCreateAndAssignToRole.pending, (state) => {
        state.bulkAssignStatus.loading = true;
        state.bulkAssignStatus.error = null;
        state.bulkAssignStatus.success = false;
      })
      .addCase(bulkCreateAndAssignToRole.fulfilled, (state, action) => {
        state.bulkAssignStatus.loading = false;
        state.bulkAssignStatus.success = true;
        state.permissions = [
          ...state.permissions,
          ...action.payload.permissions
        ];
      })
      .addCase(bulkCreateAndAssignToRole.rejected, (state, action) => {
        state.bulkAssignStatus.loading = false;
        state.bulkAssignStatus.error = action.payload;
        state.bulkAssignStatus.success = false;
      });
  }
});

export const {
  setCurrentPermission,
  clearPermissionError,
  setModuleFilters
} = permissionSlice.actions;

export const permission = permissionSlice.reducer;
export default permission;


// Selectors
export const selectBulkAssignStatus = (state) => state.permission.bulkAssignStatus;
export const selectPermissions = (state) => state.permission.permissions;